import { useQuery } from "@tanstack/react-query";
import { useConfig } from "wagmi";
import { getPublicClient } from "wagmi/actions";
import type { Address } from "viem";
import type { RedKeel } from "../config/networks";

const NPM_URI_ABI = [
  { type: "function", name: "tokenURI", stateMutability: "view",
    inputs: [{ type: "uint256" }], outputs: [{ type: "string" }] },
] as const;

/**
 * La estampa del NFT de la posición, la que dibuja el propio NonfungiblePositionManager.
 *
 * No es adorno: es la prueba visible de que la posición existe en Uniswap y es del vault. El
 * `tokenURI` lo genera el contrato on-chain —con el par, la comisión, los bordes del rango y el
 * tokenId dentro del SVG—, así que lo que se ve sale de la misma fuente que el dinero.
 *
 * DE DÓNDE VIENE ESTO Y POR QUÉ SE FILTRA. El `tokenURI` es una cadena que devuelve un contrato de
 * la cadena, y en Robinhood y HyperEVM el NPM no está en ninguna lista blanca: hay que tratarlo
 * como texto de fuera. Por eso solo se acepta `data:image/…` o `https://…`, y quien lo pinta es un
 * `<img src>` de React — nunca `innerHTML` ni un SVG en línea, que sí ejecutaría scripts.
 *
 * Si algo no cuadra se devuelve `null` y la tarjeta no se pinta: una posición sin estampa se sigue
 * gestionando igual, y es mejor que enseñar un hueco roto.
 */
export function useNft(npm: Address | undefined, tokenId: bigint, red: RedKeel | undefined) {
  const config = useConfig();

  return useQuery({
    queryKey: ["nft", red?.clave, npm, tokenId.toString()],
    enabled: !!npm && !!red && tokenId !== 0n,
    staleTime: 5 * 60_000,   // la estampa solo cambia si cambia la posicion
    queryFn: async (): Promise<{ imagen: string; nombre?: string } | null> => {
      const client = getPublicClient(config, { chainId: red!.idCadena });
      if (!client) return null;

      const uri = await client.readContract({
        address: npm!, abi: NPM_URI_ABI, functionName: "tokenURI", args: [tokenId],
      });
      if (!uri || !uri.includes(",")) return null;

      // `data:application/json;base64,<…>` — lo que devuelve el NPM de Uniswap.
      const carga = uri.slice(uri.indexOf(",") + 1);
      let json: { image?: string; name?: string };
      try {
        json = JSON.parse(
          uri.startsWith("data:application/json;base64,")
            ? new TextDecoder().decode(Uint8Array.from(atob(carga), (c) => c.charCodeAt(0)))
            : decodeURIComponent(carga)
        );
      } catch {
        return null;
      }

      const imagen = String(json.image ?? "");
      if (!/^(data:image\/|https:\/\/)/i.test(imagen)) return null;
      return { imagen, nombre: json.name };
    },
    retry: false,
  });
}
